import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import the mock majors data
import { mockMajors } from '../MajorsList/MajorsList';

const MajorSemesterSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedMajorId = queryParams.get('majorId') ? parseInt(queryParams.get('majorId')) : null;
  
  const [majors, setMajors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMajor, setSelectedMajor] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    // Simulate API call to fetch majors
    setTimeout(() => {
      setMajors(mockMajors);
      
      // If a majorId was provided in the URL, set it as selected
      if (preselectedMajorId) {
        const major = mockMajors.find(m => m.id === preselectedMajorId);
        if (major) {
          setSelectedMajor(major);
        }
      }
      
      setLoading(false);
    }, 800);
  }, [preselectedMajorId]);

  const handleMajorChange = (e) => {
    const majorId = parseInt(e.target.value);
    const major = majors.find(m => m.id === majorId);
    setSelectedMajor(major || null);
    
    // Reset semester if it's greater than the new major's total semesters
    if (major && selectedSemester > major.totalSemesters) {
      setSelectedSemester(1);
    }
    
    // Clear any errors
    setFormErrors({
      ...formErrors,
      major: undefined
    });
  };

  const handleSemesterChange = (e) => {
    setSelectedSemester(parseInt(e.target.value));
    
    // Clear any errors
    setFormErrors({
      ...formErrors,
      semester: undefined
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!selectedMajor) {
      errors.major = "Please select a major";
    }
    
    if (!selectedSemester) {
      errors.semester = "Please select your current semester";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, this would send the data to the server
      console.log('Selected Major:', selectedMajor);
      console.log('Selected Semester:', selectedSemester);
      
      // Show success message
      setFormSubmitted(true);
      
      // In a real application, you might navigate to another page or update the user profile
      // navigate('/dashboard');
    }
  };

  const calculateYearAndSemester = (semesterNumber) => {
    if (!semesterNumber) return '';
    
    const year = Math.ceil(semesterNumber / 2);
    const semesterInYear = semesterNumber % 2 === 0 ? 2 : 1;
    
    return `Year ${year}, Semester ${semesterInYear}`;
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (formSubmitted) {
    return (
      <div className="container">
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          marginTop: '2rem',
          backgroundColor: '#f0fff4' 
        }}>
          <h2 style={{ color: 'var(--success-color)', marginBottom: '1rem' }}>
            Academic Profile Updated Successfully!
          </h2>
          <p style={{ marginBottom: '1.5rem' }}>
            You have successfully updated your academic profile to:
          </p>
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
              {selectedMajor.name}
            </p>
            <p>
              Current Semester: {selectedSemester} ({calculateYearAndSemester(selectedSemester)})
            </p>
          </div>
          <p>
            This information will be used to provide you with personalized internship recommendations 
            and academic resources relevant to your stage of study.
          </p>
          
          <div style={{ marginTop: '2rem' }}>
            <button 
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2>Select Your Major and Semester</h2>
        <p>This information helps us customize your experience and provide relevant opportunities</p>
      </div>

      <div className="card" style={{ padding: '2rem', maxWidth: '700px', margin: '0 auto' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="major">Select Your Major</label>
            <select 
              id="major"
              value={selectedMajor ? selectedMajor.id : ''}
              onChange={handleMajorChange}
              className={formErrors.major ? 'error' : ''}
            >
              <option value="">-- Select a Major --</option>
              {majors.map(major => (
                <option key={major.id} value={major.id}>
                  {major.name} ({major.faculty})
                </option>
              ))}
            </select>
            {formErrors.major && <div className="error-message">{formErrors.major}</div>}
          </div>
          
          {selectedMajor && (
            <div style={{ marginBottom: '2rem' }}>
              <label htmlFor="semester">Current Semester</label>
              <select 
                id="semester"
                value={selectedSemester}
                onChange={handleSemesterChange}
                className={formErrors.semester ? 'error' : ''}
              >
                {Array.from({ length: selectedMajor.totalSemesters }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    Semester {i + 1} ({calculateYearAndSemester(i + 1)})
                  </option>
                ))}
              </select>
              {formErrors.semester && <div className="error-message">{formErrors.semester}</div>}
            </div>
          )}
          
          {selectedMajor && (
            <div className="card" style={{ 
              padding: '1.5rem', 
              backgroundColor: 'var(--light-color)',
              marginBottom: '2rem'
            }}>
              <h3>Selected Major Information</h3>
              <p><strong>Major:</strong> {selectedMajor.name}</p>
              <p><strong>Faculty:</strong> {selectedMajor.faculty}</p>
              <p><strong>Total Duration:</strong> {selectedMajor.totalSemesters} semesters ({Math.ceil(selectedMajor.totalSemesters/2)} years)</p>
              <p><strong>Your Current Status:</strong> {calculateYearAndSemester(selectedSemester)}</p>
              
              <div style={{ marginTop: '1rem' }}>
                <p><strong>Progress:</strong></p>
                <div style={{ 
                  backgroundColor: '#e9ecef',
                  height: '20px',
                  borderRadius: '10px',
                  overflow: 'auto',
                  marginTop: '0.5rem'
                }}>
                  <div style={{ 
                    width: `${(selectedSemester / selectedMajor.totalSemesters) * 100}%`,
                    backgroundColor: 'var(--primary-color)',
                    height: '100%',
                    borderRadius: '10px',
                    transition: 'width 0.5s ease-in-out'
                  }}></div>
                </div>
                <p style={{ 
                  textAlign: 'right', 
                  fontSize: '0.9rem', 
                  marginTop: '0.25rem' 
                }}>
                  {Math.round((selectedSemester / selectedMajor.totalSemesters) * 100)}% completed
                </p>
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button 
              type="submit"
              className="btn-primary"
              disabled={!selectedMajor}
            >
              Save Academic Information
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MajorSemesterSelection;